// The providers a punter can connect, and the keys they connect them with.
//
// A key lives in one place: this device. It is sent, per request, in a header
// the server uses and throws away — that is what public/privacy.html promises,
// and it is why there is no "sync my settings" here and no key in any payload
// that gets stored.
//
// The list is duplicated from server/providers/index.mjs on purpose. Settings
// has to render before any network call resolves — a first-run phone with no
// key has nothing to ask the server WITH — so the names, blurbs and signup
// links ship in the bundle. GET /api/providers is the authority on
// capabilities; this list is the authority on how they are described.

import { ref } from 'vue';
import { prefs } from '../core/storage.js';
import { apiBase as sharedApiBase } from './api-base.js';

/** Where the per-provider keys and the cached credit counts live in `prefs`. */
export const KEYS_PREF = 'pref.providerKeys';
export const SESSIONS_PREF = 'pref.providerSessions';
export const CREDITS_PREF = 'pref.providerCredits';
export const MARKET_BOOK_PREF = 'pref.marketBook';
const LEGACY_KEY_PREF = 'pref.oddsApiKey';

/** The one every fetch falls back to. */
export const DEFAULT_PROVIDER = 'the-odds-api';

export const PROVIDERS = [
  {
    id: 'the-odds-api',
    name: 'The Odds API',
    signupUrl: 'https://the-odds-api.com',
    blurb: 'Live odds from Australian bookmakers. Free 500 credits a month.',
    purpose: 'The board. Australian bookmakers\' head-to-head prices (Sportsbet, TAB, Ladbrokes, Neds, Bet365, the Betfair Exchange and more) plus scores. This is the one key Legwork needs: without it there is no pad.',
    capabilities: { odds: true, scores: true, historical: true, sharp: false, exchange: false, free: true },
    keyed: true,
  },
  {
    id: 'oddspapi',
    name: 'OddsPapi',
    signupUrl: 'https://oddspapi.io',
    blurb: 'Adds Pinnacle, the sharpest book. Free 250 requests a month.',
    purpose: 'The sharp price. Adds Pinnacle, the bookmaker whose prices are closest to fair, so the Sharp Anchor strategy runs and Pinnacle can stand in for the market in the setting below.',
    capabilities: { odds: true, scores: false, historical: true, sharp: true, exchange: false, free: true },
    keyed: true,
  },
  {
    id: 'sportsgameodds',
    name: 'SportsGameOdds',
    signupUrl: 'https://sportsgameodds.com',
    // Measured on 2026-09-13 with a free key: 2,500 objects a month, eight
    // leagues (NBA, NFL, MLB, NHL, MLS, NCAA, Champions League) and no
    // Australian sport at all — AFL, NRL, A-League, NBL and BBL are refused as
    // unknown league ids, not as a plan lock. Say so before somebody pastes a key.
    blurb: 'US leagues and the Champions League on the free plan, Pinnacle among the books. No Australian sport without a paid plan.',
    purpose: 'US leagues and the Champions League (NBA, NFL, MLB, NHL, MLS, NCAA, UCL) with scores, Pinnacle among the books. The free plan carries no Australian sport at all.',
    capabilities: { odds: true, scores: true, historical: true, sharp: true, exchange: false, free: true },
    keyed: true,
  },
  {
    id: 'squiggle',
    name: 'Squiggle',
    signupUrl: 'https://api.squiggle.com.au/',
    blurb: 'Free AFL fixtures, results and a public model ensemble. No key needed.',
    purpose: 'AFL fixtures, results and the public tipping models, free, no key. It settles AFL bets and feeds the Squiggle model.',
    capabilities: { odds: false, scores: true, historical: true, sharp: false, exchange: false, free: true },
    keyed: false,
  },
  {
    id: 'betfair-exchange',
    name: 'Betfair Exchange',
    signupUrl: 'https://developer.betfair.com/en/get-started/',
    blurb: 'The real market price, with no bookmaker margin in it. Free delayed key.',
    purpose: 'The exchange\'s own prices, with no bookmaker margin in them: the best measure of a fair price there is. It adds a Betfair column to every match on the board, gives Mispriced odds a sharp opinion to argue with, and is what brings Sharp Anchor out of Labs.',
    capabilities: { odds: true, scores: false, historical: false, sharp: true, exchange: true, free: false },
    keyed: true,
    // The one provider that needs a login as well as a key: the app key says
    // which application is asking, the session says which punter. See
    // lib/betfair.js and server/betfair.mjs.
    session: true,
  },
  {
    id: 'puntersedge',
    name: 'PuntersEdge',
    signupUrl: 'https://puntersedge.online/api',
    blurb: 'Australian and New Zealand racing, fourteen books on every runner. Free 1,500 credits a month.',
    purpose: 'The racing card. Horses, greyhounds and harness from every AU and NZ meeting, with every '
      + 'bookmaker\'s price on every runner, scratchings, track condition and the real jump time. It is the '
      + 'only key the Racing tab needs: without it there is no card.',
    // `racing` is a capability no other adapter declares, and it is the honest
    // one: this provider prices no football and answers no scores, so it is
    // never offered as the board.
    capabilities: {
      odds: false, scores: false, racing: true, historical: true, sharp: false, exchange: false, free: true,
    },
    keyed: true,
  },
];

export const providerById = (id) => PROVIDERS.find((p) => p.id === id) ?? null;

/** The header one provider's key travels in. Must match the adapter's keyHeader. */
export const keyHeaderFor = (id) => `X-Provider-Key-${id}`;

/** And the header its session token travels in. Must match `sessionHeader`. */
export const sessionHeaderFor = (id) => `X-Provider-Session-${id}`;

// --- the keys -----------------------------------------------------------------

function readKeys() {
  const stored = prefs[KEYS_PREF];
  return stored && typeof stored === 'object' && !Array.isArray(stored) ? { ...stored } : {};
}

export function getProviderKey(id) {
  const key = readKeys()[id];
  return typeof key === 'string' ? key : '';
}

export function setProviderKey(id, key) {
  const trimmed = String(key ?? '').trim();
  if (!trimmed) return clearProviderKey(id);
  prefs[KEYS_PREF] = { ...readKeys(), [id]: trimmed };
  return trimmed;
}

export function clearProviderKey(id) {
  const all = readKeys();
  delete all[id];
  prefs[KEYS_PREF] = all;
  // A session belongs to the key that opened it. Forgetting the key and
  // leaving the token behind would leave a header going up that nothing on the
  // other side can use, and a Settings line saying "Session until 9:42 pm"
  // under an empty field.
  clearProviderSession(id);
  return '';
}

/** Every provider with a key on this device. */
export const connectedProviders = () => Object.keys(readKeys()).filter((id) => providerById(id));

/**
 * The one-time move of the old single key into the map.
 *
 * It runs at import, so nothing downstream has to remember to call it, and it
 * is idempotent: the old pref is deleted, and a second run finds nothing. An
 * existing key must survive this — the app has been shipping with one, and a
 * migration that loses it locks a paying user out of their own odds.
 */
export function migrateLegacyKey() {
  const legacy = prefs[LEGACY_KEY_PREF];
  if (typeof legacy !== 'string' || !legacy.trim()) return false;
  const all = readKeys();
  if (!all[DEFAULT_PROVIDER]) prefs[KEYS_PREF] = { ...all, [DEFAULT_PROVIDER]: legacy.trim() };
  delete prefs[LEGACY_KEY_PREF];
  return true;
}

/**
 * The key headers for a fetch. One per connected provider, plus the legacy
 * `X-Odds-Api-Key` for The Odds API — old servers only understand that one, and
 * a phone can be newer than the server it is talking to.
 *
 * A provider with a live session sends that too, in its own header. An expired
 * one is not sent at all: a token the server would only be refused for is a
 * secret travelling for nothing.
 */
export function keyHeaders({ now = Date.now() } = {}) {
  const headers = {};
  for (const [id, key] of Object.entries(readKeys())) {
    if (!key || !providerById(id)) continue;
    headers[keyHeaderFor(id)] = key;
    const session = getProviderSession(id, now);
    if (session) headers[sessionHeaderFor(id)] = session.token;
  }
  const legacy = getProviderKey(DEFAULT_PROVIDER);
  if (legacy) headers['X-Odds-Api-Key'] = legacy;
  return headers;
}

// --- the sessions -------------------------------------------------------------
//
// One provider has one. A Betfair session is a token with a clock on it, so
// what is stored is `{ token, expiresAt }` and everything that reads it reads
// the clock too — a stale token in a header is an error the punter has to be
// told about, and the honest place to notice is here rather than there.

function readSessions() {
  const stored = prefs[SESSIONS_PREF];
  return stored && typeof stored === 'object' && !Array.isArray(stored) ? { ...stored } : {};
}

/** One provider's live session, or null when there is none or it has run out. */
export function getProviderSession(id, now = Date.now()) {
  const stored = readSessions()[id];
  const token = typeof stored?.token === 'string' ? stored.token.trim() : '';
  if (!token) return null;
  const at = Date.parse(stored.expiresAt ?? '');
  if (Number.isFinite(at) && at <= now) return null;
  return { token, expiresAt: stored.expiresAt ?? null };
}

export function setProviderSession(id, { token, expiresAt } = {}) {
  const trimmed = String(token ?? '').trim();
  if (!trimmed) return clearProviderSession(id);
  const session = { token: trimmed, expiresAt: expiresAt ?? null };
  prefs[SESSIONS_PREF] = { ...readSessions(), [id]: session };
  return session;
}

export function clearProviderSession(id) {
  const all = readSessions();
  if (!Object.hasOwn(all, id)) return null;
  delete all[id];
  prefs[SESSIONS_PREF] = all;
  return null;
}

// --- credits ------------------------------------------------------------------

/** The last credit counts we were told about, by provider id. Cached, not live. */
export const providerCredits = ref((() => {
  const stored = prefs[CREDITS_PREF];
  return stored && typeof stored === 'object' && !Array.isArray(stored) ? { ...stored } : {};
})());

export function setProviderCredits(id, credits) {
  if (!credits || (credits.remaining === null && credits.used === null)) return;
  providerCredits.value = { ...providerCredits.value, [id]: credits };
  prefs[CREDITS_PREF] = providerCredits.value;
}

// --- which book is the market -------------------------------------------------

/**
 * Which book a strategy should treat as "the market": `consensus` (every book
 * that prices the whole game, devigged and averaged), `betfair_ex_au` (an
 * exchange, so no margin to strip), or `pinnacle` (the sharp line).
 *
 * Two things read it: the pad's runner context (`ctx.marketBook`, which
 * `runStrategy` measures a market-consensus edge against) and a backtest spec
 * (`spec.marketBook`, which the engine scores the run against and records in
 * the summary). Nothing else — a model strategy's edge is its model against
 * the price being taken, and Classic measures no edges at all.
 */
export const MARKET_BOOKS = ['consensus', 'betfair_ex_au', 'pinnacle'];

export function getMarketBook() {
  const stored = prefs[MARKET_BOOK_PREF];
  return MARKET_BOOKS.includes(stored) ? stored : 'consensus';
}

/**
 * The same choice as a ref, so the pad re-runs the moment Settings changes it.
 * `prefs` is plain storage and nothing watches it; this is what the store's
 * runner context reads.
 */
export const marketBook = ref(getMarketBook());

export function setMarketBook(key) {
  const next = MARKET_BOOKS.includes(key) ? key : 'consensus';
  prefs[MARKET_BOOK_PREF] = next;
  marketBook.value = next;
  return next;
}

// --- the probe ----------------------------------------------------------------

/**
 * Ask the server whether a key works. Resolves, never rejects:
 * { ok, credits, bookmakers, sports, error }.
 *
 * The key goes in the header, never the query string — a query string ends up
 * in access logs, and that is the whole thing we are trying not to do.
 */
export async function probeProvider(id, { apiBase = sharedApiBase() } = {}) {
  const provider = providerById(id);
  if (!provider) return { ok: false, credits: null, bookmakers: null, sports: null, error: 'Unknown provider.' };
  if (provider.keyed && !getProviderKey(id)) {
    return { ok: false, credits: null, bookmakers: null, sports: null, error: 'Add a key first.' };
  }

  let res;
  try {
    res = await fetch(`${apiBase}/api/providers/probe?provider=${encodeURIComponent(id)}`, {
      headers: { Accept: 'application/json', ...keyHeaders() },
    });
  } catch {
    return { ok: false, credits: null, bookmakers: null, sports: null, error: 'No connection. Try again when you are online.' };
  }

  let body = null;
  try {
    body = await res.json();
  } catch {
    body = null;
  }
  if (!body || typeof body !== 'object') {
    return { ok: false, credits: null, bookmakers: null, sports: null, error: 'The server did not answer properly.' };
  }

  const out = {
    ok: body.ok === true,
    credits: body.credits ?? null,
    bookmakers: body.bookmakers ?? null,
    sports: body.sports ?? null,
    error: body.ok === true ? '' : String(body.error ?? body.message ?? 'That key did not work.'),
  };
  if (out.ok) setProviderCredits(id, out.credits);
  return out;
}

// At import, not at mount: src/lib/odds.js reads the key while the app is
// booting, and a key still sitting in the old pref would read as no key at all.
migrateLegacyKey();
